/**
 * FloatingChatButton — animated mystical FAB (bottom-right) that opens the Delfos oracle drawer.
 * Smaller and mobile-friendly. Hidden when chat is open.
 */
import { Tooltip } from 'antd';
import { theme } from 'antd';
import { useChatContext } from '@/context/ChatContext';

const KEYFRAMES = `
@keyframes delfosOrbit {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes delfosPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(31,75,216,0.45), 0 2px 12px rgba(17,28,68,0.32); }
  50%       { box-shadow: 0 0 0 8px rgba(31,75,216,0), 0 4px 20px rgba(31,75,216,0.45); }
}
@keyframes delfosIrisBreath {
  0%, 100% { r: 6; opacity: 0.9; }
  50%       { r: 7.5; opacity: 1; }
}
`;

/** SVG oracle eye — pupil breathes, ring orbits */
function OracleEyeIcon() {
  return (
    <svg
      viewBox="0 0 36 36"
      width="22"
      height="22"
      aria-hidden="true"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <radialGradient id="dfIris" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#d7e9ff" />
          <stop offset="100%" stopColor="#1f4bd8" />
        </radialGradient>
        <radialGradient id="dfPupil" cx="50%" cy="40%" r="50%">
          <stop offset="0%" stopColor="#2341b4" />
          <stop offset="100%" stopColor="#102a8c" />
        </radialGradient>
      </defs>

      {/* Outer orbit ring — spins */}
      <g style={{ transformOrigin: '18px 18px', animation: 'delfosOrbit 6s linear infinite' }}>
        <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(124,173,255,0.55)" strokeWidth="1" strokeDasharray="4 3" />
        <circle cx="18" cy="3" r="2" fill="#7badff" opacity="0.8" />
      </g>

      {/* Eyelid shape */}
      <path
        d="M4 18 Q18 6 32 18 Q18 30 4 18 Z"
        fill="rgba(255,255,255,0.07)"
        stroke="rgba(124,173,255,0.62)"
        strokeWidth="1"
      />

      {/* Iris */}
      <circle cx="18" cy="18" r="8" fill="url(#dfIris)" />

      {/* Pupil — breathing via CSS on the group */}
      <g style={{ transformOrigin: '18px 18px', animation: 'delfosIrisBreath 2.4s ease-in-out infinite' }}>
        <circle cx="18" cy="18" r="5.5" fill="url(#dfPupil)" />
      </g>

      {/* Specular highlight */}
      <ellipse cx="20.5" cy="15.5" rx="2" ry="1.2" fill="rgba(255,255,255,0.55)" transform="rotate(-20 20.5 15.5)" />
    </svg>
  );
}

export function FloatingChatButton() {
  const { openChat, isOpen } = useChatContext();
  const { token } = theme.useToken();

  if (isOpen) return null;

  return (
    <>
      <style>{KEYFRAMES}</style>
      <Tooltip title="Delfos — Oráculo PMO" placement="left">
        <button
          onClick={openChat}
          data-tour="ai-chat-button"
          aria-label="Abrir Delfos"
          style={{
            position: 'fixed',
            bottom: 'clamp(64px, 8vw, 80px)',
            right: 'clamp(14px, 3vw, 28px)',
            zIndex: 1000,
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: `1.5px solid ${token.colorPrimaryBorder}`,
            background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            animation: 'delfosPulse 2.8s ease-in-out infinite',
            // Mobile: reduce touch target padding via padding on a wrapper isn't needed;
            // 44px meets WCAG minimum
          }}
        >
          <OracleEyeIcon />
        </button>
      </Tooltip>
    </>
  );
}
